import React from 'react';
import { Constants } from '../../../Models/Constants';
import { IDesignSession } from '../../../Models/IDesignSession';
import { AvailableProductLine } from '../../../Models/Enums';
import { ApiService } from '../../../Services/ApiService';
import { getAppInsights, getPreferredUnits, getCountry } from '../../../Services/Global';
import { haveHotMediaOutputFields, haveColdMediaOutPutFields, getPHEType } from "../../../Utils/UnitConfigurationHelper";
import { isRelevantInputField } from './ProductDesignFormUtils';
import { ProductDesignFormState, ProductDesignFormAction } from './ProductDesignFormTypes';
import { IMultiSectionUnitConfiguration } from '../../../Models/IUnitConfiguration';
import logger from '../../../Services/LoggingService';

/**
 * Transform API response from snake_case multi-section format to expected camelCase array format
 * Handles both single-section and multi-section responses
 */
function transformApiResponse(rawResult: any[], unitConfiguration?: IMultiSectionUnitConfiguration): IDesignSession[] {
  if (!rawResult || rawResult.length === 0) {
    logger.warn('Empty API response received', null, {
      component: 'ProductDesignForm',
      method: 'transformApiResponse'
    });
    return [];
  }

  const firstResult = rawResult[0];
  
  // Check if this is a multi-section response (has plate_grouping_sec properties)
  const plateGroupingKeys = Object.keys(firstResult).filter(key => key.startsWith('plate_grouping_sec'));
  
  if (plateGroupingKeys.length > 0) {
    logger.debug('Multi-section response detected', {
      sectionCount: plateGroupingKeys.length,
      keys: plateGroupingKeys
    }, {
      component: 'ProductDesignForm',
      method: 'transformApiResponse'
    });

    // Multi-section response: transform to array of IDesignSession objects
    const transformedSessions: IDesignSession[] = plateGroupingKeys.map((key) => {
      const sectionMatch = key.match(/sec(\d+)/);
      const sectionNumber = sectionMatch ? parseInt(sectionMatch[1]) : 1;
      
      return {
        sectionNumber: sectionNumber,
        phePlateType: firstResult.phe_plate_type || firstResult.phePlateType,
        platePackConfiguration: firstResult[key],
        // Copy over other properties from the response
        ...Object.keys(firstResult).reduce((acc, k) => {
          // Skip the plate_grouping_sec keys as we've already processed them
          if (!k.startsWith('plate_grouping_sec') && k !== 'phe_plate_type') {
            // Convert snake_case to camelCase
            const camelKey = k.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
            acc[camelKey] = firstResult[k];
          }
          return acc;
        }, {} as any)
      };
    }).sort((a, b) => (a.sectionNumber || 0) - (b.sectionNumber || 0));

    logger.info('Transformed multi-section response', {
      originalKeys: Object.keys(firstResult),
      transformedSections: transformedSessions.length,
      sections: transformedSessions.map(s => ({ sectionNumber: s.sectionNumber, platePackConfiguration: s.platePackConfiguration }))
    }, {
      component: 'ProductDesignForm',
      method: 'transformApiResponse'
    });

    return transformedSessions;
  } else {
    // Single section or already in correct format
    logger.debug('Single-section or already formatted response detected', {
      hasPhePlateType: !!firstResult.phePlateType,
      hasPhe_plate_type: !!firstResult.phe_plate_type
    }, {
      component: 'ProductDesignForm',
      method: 'transformApiResponse'
    });

    // Check if we need to convert from snake_case
    if (firstResult.phe_plate_type && !firstResult.phePlateType) {
      return rawResult.map(item => ({
        ...item,
        phePlateType: item.phe_plate_type,
        platePackConfiguration: item.plate_pack_configuration || item.platePackConfiguration
      }));
    }

    return rawResult as IDesignSession[];
  }
}

/**
 * Constructs the request body for the design submission
 *
 * Exported for testing: the payload shape is what ends up persisted in
 * PositionDesignSessions, so it is worth asserting on directly.
 */
export function constructRequestBody(state: ProductDesignFormState, country: string): any {
  const body: any = {};

  // Ensure all required fields for configuration lookup are present
  // Trim all string fields to prevent matching issues due to whitespace differences
  body.industry = state.unitConfiguration?.industry?.trim();
  body.subIndustry = state.unitConfiguration?.subIndustry?.trim();
  body.application = state.unitConfiguration?.application?.trim();
  body.subApplication = state.unitConfiguration?.subApplication?.trim();
  body.alfaLavalPosition = state.unitConfiguration?.alfaLavalPosition?.trim();
  body.processStage = state.unitConfiguration?.processStage?.trim();
  body.productLine = state.unitConfiguration?.productLine?.trim();
  body.flowType = state.unitConfiguration?.flowType?.trim();
  body.sectionCount = state.unitConfiguration?.sectionCount;
  body.country = country?.trim();
  // Snapshot of the unit system this form was displayed in, not the unit of the values
  // below - those are always metric (UnitAwareTextField converts before submit). Recorded
  // on the design session so usage can be analysed per unit system after the fact.
  body.userUiUnitSystem = getPreferredUnits().preferredUnits;
  body.sections = [];

  // Validate that all required fields for configuration lookup are present
  const requiredFields = ['industry', 'subIndustry', 'application', 'subApplication', 'alfaLavalPosition', 'processStage', 'productLine'];
  const missingFields = requiredFields.filter(field => !body[field]);
  
  if (missingFields.length > 0) {
    throw new Error(`Missing required fields for configuration lookup: ${missingFields.join(', ')}`);
  }

  // When creating the body, we check for which fields are used for the selected position.
  if (!state.unitConfiguration?.isMultiSection) {
    let singleSection: any = { sectionNumber: 1 };

    // Helper to map constants to state properties - manual mapping is safer to preserve exact logic
    if (isRelevantInputField(Constants.ColdMediaType, state.unitConfiguration)) singleSection.coldMediaType = parseInt(state.unitConfiguration?.coldMediaType!);
    if (isRelevantInputField(Constants.ColdMediaFlowrate, state.unitConfiguration)) singleSection.coldMediaFlowrate = parseInt(state.coldMediaFlowrate?.toString()!);
    if (isRelevantInputField(Constants.ColdMediaTempIn, state.unitConfiguration)) singleSection.coldMediaTempIn = parseFloat(state.coldMediaTempIn?.toString()!);
    if (isRelevantInputField(Constants.ColdMediaTempOut, state.unitConfiguration)) singleSection.coldMediaTempOut = parseFloat(state.coldMediaTempOut?.toString()!);
    if (isRelevantInputField(Constants.ColdMediaPressureDrop, state.unitConfiguration)) singleSection.coldMediaPressureDrop = parseFloat(state.coldMediaPressureDrop?.toString()!);

    if (isRelevantInputField(Constants.HotMediaType, state.unitConfiguration)) singleSection.hotMediaType = parseInt(state.unitConfiguration?.hotMediaType?.toString()!);
    if (isRelevantInputField(Constants.HotMediaFlowrate, state.unitConfiguration)) singleSection.hotMediaFlowrate = parseFloat(state.hotMediaFlowrate?.toString()!);
    if (isRelevantInputField(Constants.HotMediaTempIn, state.unitConfiguration)) singleSection.hotMediaTempIn = parseFloat(state.hotMediaTempIn?.toString()!);
    if (isRelevantInputField(Constants.HotMediaTempOut, state.unitConfiguration)) singleSection.hotMediaTempOut = parseFloat(state.hotMediaTempOut?.toString()!);
    if (isRelevantInputField(Constants.HotMediaPressureDrop, state.unitConfiguration)) singleSection.hotMediaPressureDrop = parseInt(state.hotMediaPressureDrop?.toString()!);

    body.sections.push(singleSection);
  }
  else {
    const unitOperationTargetValue = state.userInputs?.find(x => x.name === Constants.UnitOperationTargetValue);

    if (unitOperationTargetValue !== undefined) {
      body.UnitOperationTargetValue = unitOperationTargetValue?.value !== undefined ? parseInt(unitOperationTargetValue?.value) : 0;
    }

    state.sectionCollection.forEach((section) => {
      const sectionNumber = section.sectionNumber;
      const sectionInputs = state.userInputs?.filter(x => x.sectionNumber === sectionNumber);

      let multiSectionSection: any = { sectionNumber: sectionNumber };

      sectionInputs?.forEach((input) => {
        const propertyName = input.trimmedName;
        if (propertyName && input.value !== undefined && input.value !== null && input.value !== '') {
          multiSectionSection[propertyName] = parseFloat(input.value);
        }
      });

      body.sections.push(multiSectionSection);
    });
  }
  
  return body;
}

/**
 * Updates the state with the design result, handling warnings, copy button state, and analytics.
 */
function updateStateWithDesignResult(
  result: IDesignSession[],
  state: ProductDesignFormState,
  dispatch: React.Dispatch<ProductDesignFormAction>
) {
  const prediction = result[0];
  const appInsights = getAppInsights();

  const warningMsg = setWarningMessageForPHEType(prediction, state);
  dispatch({ type: 'SET_WARNING_MESSAGE', payload: warningMsg });
  if (warningMsg !== undefined) {
    dispatch({ type: 'SET_SHOW_WARNING_FOR_PHE', payload: true });
  }
  
  // Control Copy button based on isAvailableInALICE
  const targetedPheType = state.pheTypes?.filter(c => c.key === prediction.phePlateType)[0];
  logger.debug(`targetedPheType in the right place getPHEType(targetedPheType.key?.toLowerCase()): ${getPHEType(targetedPheType.key?.toLowerCase())}, targetedPheType.key?.toLowerCase(): ${targetedPheType.key?.toLowerCase()}`);
  if (targetedPheType) {
    let shouldDisableCopy = !targetedPheType.isAvailableInALICE;

    // Rule: Block copy if Hygienic config gets Industrial PHE (Legacy parity)
    if (state.unitConfiguration?.productLine === Constants.ProductLine_Hygienic && 
        targetedPheType.availableProductLine === AvailableProductLine.Industrial) {
        shouldDisableCopy = true;
    }

    // TEMPORARY WORKAROUND FOR BASE11/TL10P INDUSTRIAL PRODUCT LINE SPECIAL CASE TODO
    if (state.unitConfiguration?.productLine === Constants.ProductLine_Industrial &&
      (getPHEType(targetedPheType.key?.toLowerCase()) === "base11" || targetedPheType.key?.toLowerCase() === 'tl10p')) {
        shouldDisableCopy = true;
    } 
    
    dispatch({ type: 'SET_DISABLE_COPY_BUTTON', payload: shouldDisableCopy });
  }
  


  
  appInsights?.trackEvent({ name: 'useraction:DesignResponse', properties: { 'timestamp': new Date().toISOString() } });
  localStorage.setItem('modelSuggestion', JSON.stringify(result, undefined, 2));
  dispatch({ type: 'SET_DESIGN_SESSION', payload: result });
}

/**
 * Applies workaround for H5-P units with specific plate configurations in Beer Cooling processes.
 */
function applyH5PWorkaround(prediction: IDesignSession, processParam: string): IDesignSession {
  if (prediction.phePlateType !== "H5-P") return prediction;

  const platePackConfiguration = prediction.platePackConfiguration;
  const configPattern = /^(\d+)\*(\d+)([A-Za-z])\/(\d+)\*(\d+)([A-Za-z])$/;
  const match = platePackConfiguration?.match(configPattern);
  
  if (match) {
    const [, a1, a2, c1, b1, b2, c2] = match;
    const sumArea = Number(a1) * Number(a2) + Number(b1) * Number(b2);

    if (sumArea < 19 && processParam.toLowerCase() === "Beer Cooling".toLowerCase()) {
      const modifiedPrediction = { ...prediction };
      
      logger.warn(`Special Case Applied:\n\nAI Design Wizard detected H5-P with plate pack configuration below 19 plates in Beer Cooling Process.\n\nOriginal configuration: ${platePackConfiguration}\nModified configuration: ${modifiedPrediction.platePackConfiguration}\n\nThis modification ensures optimal performance for your application.`, {
        platePackConfiguration: modifiedPrediction.platePackConfiguration,
        phePlateType: modifiedPrediction.phePlateType,
        processParam: processParam
      }, {
        component: 'ProductDesignForm',
        method: 'applyH5PWorkaround'
      });

      modifiedPrediction.platePackConfiguration = `2*5${c1}/1*9${c2}`;
      logger.debug('Setting plate pack configuration', {
        platePackConfiguration: modifiedPrediction.platePackConfiguration,
        phePlateType: modifiedPrediction.phePlateType
      }, {
        component: 'ProductDesignForm',
        method: 'applyH5PWorkaround'
      });
      
      return modifiedPrediction;
    }
  }
  return prediction;
}

/**
 * Function to submit the form and handle API response
 */
export async function submitForm(
  state: ProductDesignFormState,
  dispatch: React.Dispatch<ProductDesignFormAction>,
  apiService: ApiService,
  query: URLSearchParams,
  processedQuery: string
): Promise<void> {
  const appInsights = getAppInsights();
  
  dispatch({ type: 'SET_SHOW_WARNING_DIALOG', payload: false });
  dispatch({ type: 'SET_WARNING_MESSAGE', payload: undefined});
  dispatch({ type: 'SET_SHOW_WARNING_FOR_PHE', payload: false });
  logger.debug('Form submission: Reset warning state', null, {
    component: 'ProductDesignForm',
    method: 'submitForm'
  });
  dispatch({ type: 'SET_IS_LOADING', payload: true });
  dispatch({ type: 'SET_DESIGN_RECEIVED', payload: false });
  dispatch({ type: 'SET_SHOW_XML_MESSAGE', payload: false });
  dispatch({ type: 'SET_DISABLE_COPY_BUTTON', payload: false });
  dispatch({ type: 'SET_XML_CONTENT_READY', payload: false });
  dispatch({ type: 'SET_IS_COPY_LOADING', payload: false });

  // Get country from URL, fallback to localStorage, then to 'XX'
  const country = (query.get('country') || getCountry() || 'XX') as string;
  let body: any = {};

  try {
    body = constructRequestBody(state, country);
    
    // Debug: Log the request body to help diagnose issues
    if (state.unitConfiguration?.isMultiSection) {
      logger.debug('Submitting form with body', {
        body: JSON.stringify(body, null, 2),
        sectionCount: body.sections?.length,
        hasUnitOperationTargetValue: body.UnitOperationTargetValue !== undefined
      }, {
        component: 'ProductDesignForm',
        method: 'submitForm'
      });
    }

    const response = await apiService.postAsync(body, `designsuggestion/design`, undefined, () => { dispatch({ type: 'SET_IS_LOADING', payload: false }); dispatch({ type: 'SET_DESIGN_RECEIVED', payload: false }); });
    
    if (!response.ok) {
      dispatch({ type: 'SET_IS_LOADING', payload: false });
      throw new Error(`API request failed with status ${response.status}: ${response.statusText}`);
    }
    
    appInsights?.trackEvent({ name: 'useraction:DesignSubmit', properties: { 'timestamp': new Date().toISOString() } });
    
    const rawResult: any[] = await response.json();
  
    // Transform API response to expected format (handles both single and multi-section responses)
    let result: IDesignSession[] = transformApiResponse(rawResult, state.unitConfiguration);

    if (result.length > 0) {
      // Hardcoded workaround for H5-P below 19 plates
      const processParam = processedQuery || '';
      
      // If workaround is applied, we use the modified result for the first item
      const modifiedPrediction = applyH5PWorkaround(result[0], processParam);
      if (modifiedPrediction !== result[0]) {
        result = [modifiedPrediction, ...result.slice(1)];
      }

      updateStateWithDesignResult(result, state, dispatch);

      dispatch({ type: 'SET_HAVE_COLD_MEDIA_OUTPUTS', payload: haveColdMediaOutPutFields(state.unitConfiguration!)});
      dispatch({ type: 'SET_HAVE_HOT_MEDIA_OUTPUTS', payload: haveHotMediaOutputFields(state.unitConfiguration!)});
      dispatch({ type: 'SET_RESPONSE_RECEIVED', payload: true });
      dispatch({ type: 'SET_IS_LOADING', payload: false });
      dispatch({ type: 'SET_DESIGN_RECEIVED', payload: true });
      dispatch({ type: 'SET_SUBMIT_TEXT', payload: 'Recalculate' });
    }
  } catch (error) {
    let handled = false;

    // Check for specific 500 error from model endpoint
    if (error instanceof Error) {
      try {
        // Attempt to parse the error message as JSON (ApiService returns raw body for 500s)
        const errorObj = JSON.parse(error.message);
        
        if (errorObj.status === 500 && errorObj.title && errorObj.title.includes('model endpoint')) {
          const position = state.unitConfiguration?.alfaLavalPosition || 'the selected position';
          const friendlyMessage = `Prediction for ${position} is currently unavailable, please try again later.`;

          // Extract details for structured logging
          // The title format is typically: "An error occurred... (host)\nURL: ...\nAPI key name: ..."
          const urlMatch = errorObj.title.match(/URL: (.*?)(?:\n|$)/);
          const apiKeyMatch = errorObj.title.match(/API key name: (.*?)(?:\n|$)/);
          const hostMatch = errorObj.title.match(/No such host is known\. \((.*?)\)/);

          logger.error(`Model endpoint unavailable: ${errorObj.title.split('\n')[0]}`, error, {
            endpointUrl: urlMatch ? urlMatch[1] : 'unknown',
            apiKeyName: apiKeyMatch ? apiKeyMatch[1] : 'unknown',
            host: hostMatch ? hostMatch[1] : 'unknown',
            instance: errorObj.instance,
            traceId: errorObj.traceId,
            status: errorObj.status,
            fullError: errorObj
          }, {
            component: 'ProductDesignForm',
            method: 'submitForm'
          });

          // Show friendly UI message
          dispatch({ 
            type: 'SET_WARNING_MESSAGE', 
            payload: <div>{friendlyMessage}</div> 
          });
          dispatch({ type: 'SET_SHOW_WARNING_DIALOG', payload: true });
          
          handled = true;
        }
      } catch (e) {
        // Not JSON or parsing failed, proceed to standard error handling
      }
    }

    if (!handled) {
      if (error instanceof Error && error.message.startsWith('Missing required fields')) {
        logger.error(error.message, error, {
          unitConfiguration: state.unitConfiguration
        }, {
          component: 'ProductDesignForm',
          method: 'submitForm'
        });
      } else {
        logger.error('Form submission failed', error as Error, {
          body: body,
          component: 'ProductDesignForm',
          method: 'submitForm'
        });
      }
      dispatch({ type: 'SET_IS_LOADING', payload: false });
      dispatch({ type: 'SET_DESIGN_RECEIVED', payload: false });
      throw error;
    }

    // If handled, ensure state is reset but do NOT rethrow
    dispatch({ type: 'SET_IS_LOADING', payload: false });
    dispatch({ type: 'SET_DESIGN_RECEIVED', payload: false });
  }
}

/**
 * Function to generate warning messages for PHE types
 * 
 */
export function setWarningMessageForPHEType(result: IDesignSession, state: ProductDesignFormState): JSX.Element | undefined {
  const preferredUnit = getPreferredUnits().preferredUnits;
  const pressureUnit = preferredUnit === 'us' ? 'PSI' : 'kPa';

  if (!state.pheTypes || state.pheTypes.length === 0) {
    logger.warn('[setWarningMessageForPHEType] No PHE types available in state');
    return undefined;
  }

  // Get the PHE type from database configuration
  const targetedPheType = state.pheTypes.filter(c => c.key === result.phePlateType)[0];
  
  if (!targetedPheType) {
    logger.warn('[setWarningMessageForPHEType] Targeted PHE type not found in state', result.phePlateType);
    return undefined;
  }

  // Diagnostic logging for PHE type configuration (TEMPORARY - for debugging BASE11 issue)
  const diagnosticInfo = {
    key: targetedPheType.key,
    text: targetedPheType.text,
    isAvailableInALICE: targetedPheType.isAvailableInALICE,
    availableProductLine: targetedPheType.availableProductLine,
    informationForUser: targetedPheType.informationForUser ? 'Present' : 'Empty',
    currentProductLine: state.unitConfiguration?.productLine
  };
  
  logger.info('[setWarningMessageForPHEType] PHE type found', diagnosticInfo);

  // PRIORITY 1: Custom rules
  
  // Special case for obsolete M10M (Hygienic CIP Utility Heater)
  if (state.unitConfiguration?.productLine === Constants.ProductLine_Hygienic && 
    state.unitConfiguration.processStage === "CIP" && 
    state.unitConfiguration.alfaLavalPosition === "Utility Heater") {
    if (result.phePlateType === "M10M" || result.phePlateType === "dual_inlet_M10M") {
      let body = encodeURIComponent(`Dear support, \n
          Please support with the selection of an appropriate PHE design for the following duty 
          Steam temperature inlet ${result.hotMediaTempIn ?? ' '} ${preferredUnit === 'us' ? '°F' : '°C'}
          Steam temperature outlet ${result.hotMediaTempOut ?? ' '} ${preferredUnit === 'us' ? '°F' : '°C'}
          
          Water flowrate ${result.coldMediaFlowrate ?? ' '} ${pressureUnit}
          Water temperature inlet ${result.coldMediaTempIn ?? ' '} ${preferredUnit === 'us' ? '°F' : '°C'}
          Water temperature outlet ${result.coldMediaTempOut ?? ' '} ${preferredUnit === 'us' ? '°F' : '°C'}

          I have used the AI design support and received the following design suggestion with an PHE type that is marked for obsoletion.
          ${result.phePlateType ?? ' No Phe plate type '} ${result.platePackConfiguration ?? ' No plate pack configuration'}

          Please support with a PHE design from the current product range.`);
      return (
        <div>
            AI Design Wizard has recommended an M10M unit for this duty. M10M is marked as an obsolete unit type.
            <br />It is recommended that you contact <a href={`mailto:DairyGPHE.Technical@alfalaval.com?subject=Support on PHE design for a dairy CIP utility steam heater&body=${body}`}>DairyGPHE.Technical@alfalaval.com</a> to
            receive a PHE design from the current product range.
       </div>
      )
    }
  }

  // PRIORITY 1: Special case for BASE11 (Industrial TL10P version)
  // Workaround hardcoded for Industrial TL10P version which maps to BASE11
  if (state.unitConfiguration?.productLine === Constants.ProductLine_Industrial && 
      (getPHEType(targetedPheType.key?.toLowerCase()) === "base11" || targetedPheType.key?.toLowerCase() === 'tl10p')) {
    logger.info('🔍 Warning Message: BASE11/TL10P special case (Industrial PHE configurator)');
    return (
      <div>
          SC: AI Design Wizard has recommended a {getPHEType(targetedPheType.key)} which is available in CAS.
          <br />Copy the plate pack configuration and given temperatures and flow rates to use in the CAS tool to configure the heat exchanger.
      </div>
    );
  }

  // PRIORITY 2: Custom InformationForUser message
  if (targetedPheType.informationForUser && targetedPheType.informationForUser.trim() !== '') {
    logger.info('🔍 Warning Message: Using custom InformationForUser');
    return (
      <div>
          {targetedPheType.informationForUser}
      </div>
    );
  }

  // PRIORITY 3: Check IsAvailableInALICE
  // 0 = Direct to CAS, 1 = Available in ALICE (no warning)
  if (!targetedPheType.isAvailableInALICE) {
    logger.info('🔍 Warning Message: Directing to CAS (isAvailableInALICE=false)');
    return (
      <div>
          AI Design Wizard has recommended a {getPHEType(targetedPheType.text)} which is not available in ALICE.
          <br />Copy the plate pack configuration and given temperatures and flow rates to use in the CAS tool to configure the heat exchanger.
      </div>
    );
  }

  // PRIORITY 4: Check AvailableProductLine mismatch
  // 0 = Industrial only, 1 = Hygienic only, NULL = Both (no restriction)
  if (targetedPheType.availableProductLine !== null && targetedPheType.availableProductLine !== undefined) {
    if (state.unitConfiguration?.productLine === Constants.ProductLine_Industrial && 
        targetedPheType.availableProductLine === AvailableProductLine.Hygienic) {
      logger.info('🔍 Warning Message: ProductLine mismatch (Industrial config, Hygienic-only PHE)');
      return (
        <div>
            AI Design Wizard has recommended a PHE type that is not available for the Industrial line.
            <br />It is recommended that you use Hygienic plate-and-frame heat exchanger configurator while copying the model response.
        </div>
      );
    }
    
    if (state.unitConfiguration?.productLine === Constants.ProductLine_Hygienic && 
        targetedPheType.availableProductLine === AvailableProductLine.Industrial) {
      logger.info('🔍 Warning Message: ProductLine mismatch (Hygienic config, Industrial-only PHE) - Directing to CAS');
      return (
        <div>
            AI Design Wizard has recommended a {getPHEType(targetedPheType.text)} which is not available for the Hygienic PHE configurator.
            <br />Copy the plate pack configuration and given temperatures and flow rates to use in the CAS tool to configure the heat exchanger.
        </div>
      );
    }
  }

  // If we reach here, PHE type IS available in ALICE
  logger.info('🔍 Warning Message: No warning (PHE type available in ALICE)');
  
  // Return undefined if no warning conditions are met (PHE type is available in ALICE)
  return undefined;
}

/**
 * Function to copy content to clipboard
 */
export async function copyToClipBoard(
  state: ProductDesignFormState,
  dispatch: React.Dispatch<ProductDesignFormAction>,
  apiService: ApiService
): Promise<void> {
  const appInsights = getAppInsights();
 
  dispatch({ type: 'SET_SHOW_ADDITIONAL_STEPS', payload: false });
  dispatch({ type: 'SET_IS_COPY_LOADING', payload: true });
  
  appInsights?.trackEvent({ name: 'useraction:DesignCopy', properties: { 'timestamp': new Date().toISOString() } });
  
  try {
    if (localStorage.getItem('modelSuggestion') === null || localStorage.getItem('modelSuggestion') === undefined) {
      dispatch({ type: 'SET_XML_MESSAGE', payload: 'The output does not match a supported model in ALICE. To configure the heat exchanger fully, use these outputs manually in CAS before proceeding to ALICE.' });
      throw new Error('CAS content not found. Nothing was copied to clipboard.');
    }
    else {
      const response = await apiService.postAsync(state.designSession, `designsuggestion/designxml`);
      if (!response.ok) {
        const res = await response.text();
        throw Error(`Could not send response. ${response.status} ${res}`);
      }
      const result: { responseMessage: string, usable: boolean, xml: string } = await response.json();
      const targetedPheType = state.pheTypes.filter(c => c.key === state.designSession[0].phePlateType)[0];
      
      // PRIORITY: Check frontend validation first
      // If setWarningMessageForPHEType already identified an issue, override backend's usable flag
      let finalUsable = result.usable;
      let frontendOverride = false;
      
      // Check for M10M obsolete special case (Hygienic CIP Utility Heater)
      if (state.unitConfiguration?.productLine === Constants.ProductLine_Hygienic && 
          state.unitConfiguration.processStage === "CIP" && 
          state.unitConfiguration.alfaLavalPosition === "Utility Heater") {
        if (state.designSession[0].phePlateType === "M10M" || state.designSession[0].phePlateType === "dual_inlet_M10M") {
          finalUsable = false;
          frontendOverride = true;
          logger.info('[copyToClipBoard] Frontend override: M10M obsolete unit detected - forcing usable=false');
        }
      }
      
      // Check for BASE11 special case (Industrial product line)
      // TL10P key maps to BASE11 in getPHEType
      if (targetedPheType && 
          state.unitConfiguration?.productLine === Constants.ProductLine_Industrial && 
          (getPHEType(targetedPheType.key) === "BASE11" || targetedPheType.key?.toLowerCase() === 'tl10p')) {
        finalUsable = false;
        frontendOverride = true;
        logger.info('[copyToClipBoard] Frontend override: BASE11/TL10P detected for Industrial line - forcing usable=false');
      }
      
      // Check if PHE type is not available in ALICE (should use CAS instead)
      if (targetedPheType && !targetedPheType.isAvailableInALICE) {
        finalUsable = false;
        frontendOverride = true;
        logger.info('[copyToClipBoard] Frontend override: PHE type not available in ALICE - forcing usable=false');
      }
      
      // Check for custom InformationForUser message (usually indicates special handling needed)
      if (targetedPheType && targetedPheType.informationForUser && targetedPheType.informationForUser.trim() !== '') {
        finalUsable = false;
        frontendOverride = true;
        logger.info('[copyToClipBoard] Frontend override: Custom InformationForUser present - forcing usable=false');
      }
      
      // Check for Hygienic config with Industrial-only PHE (should use CAS)
      if (targetedPheType && 
          state.unitConfiguration?.productLine === Constants.ProductLine_Hygienic && 
          targetedPheType.availableProductLine === AvailableProductLine.Industrial) {
        finalUsable = false;
        frontendOverride = true;
        logger.info('[copyToClipBoard] Frontend override: Hygienic config with Industrial-only PHE - forcing usable=false');
      }
      
      // Diagnostic logging for XML copy result
      const copyDiagnosticInfo = {
        backendUsable: result.usable,
        frontendUsable: finalUsable,
        frontendOverride: frontendOverride,
        hasResponseMessage: !!result.responseMessage,
        responseMessage: result.responseMessage,
        pheTypeKey: state.designSession[0].phePlateType,
        targetedPheTypeInState: targetedPheType ? {
          key: targetedPheType.key,
          text: targetedPheType.text,
          isAvailableInALICE: targetedPheType.isAvailableInALICE,
          availableProductLine: targetedPheType.availableProductLine
        } : 'NOT_FOUND'
      };
      
      logger.info('[copyToClipBoard] Backend response with frontend validation:', copyDiagnosticInfo);

      dispatch({ type: 'SET_IS_XML_USABLE', payload: finalUsable });
      
      // Determine the content to copy based on final usability decision
      const contentToCopy = finalUsable ? result.xml : result.responseMessage;
      
      // Set content and message
      dispatch({ type: 'SET_IOS_CONTENT', payload: contentToCopy });
      
      // TEMPORARY: Commented out to test warning message from Calculate
      // The warning message should already be displayed from setWarningMessageForPHEType
      if (finalUsable) {
        // enable copy button
        logger.info('[copyToClipBoard] Displaying ALICE success message');
        dispatch({ type: 'SET_XML_MESSAGE', payload: Constants.SuccessMessageForXMLCopy });
      
      
      dispatch({ type: 'SET_SHOW_XML_MESSAGE', payload: true });
      dispatch({ type: 'SET_XML_CONTENT_READY', payload: true });
      
      // Use setTimeout to ensure DOM has been updated with new content
      setTimeout(() => {
        iosCopyToClipboard(contentToCopy);
        
        // Track successful copy operation
        if (state.designSession && state.designSession.length > 0 && state.designSession[0].id) {
          trackDesignCopySuccess(state.designSession[0].id, apiService);
        }
      }, 100);
    } 
  } 
  } catch (error) {
    logger.error('Copy operation failed', error as Error, {
      component: 'ProductDesignForm',
      method: 'copyToClipBoard'
    });
    dispatch({ type: 'SET_XML_MESSAGE', payload: 'Failed to copy content. Please try again.' });
    dispatch({ type: 'SET_SHOW_XML_MESSAGE', payload: true });
  } finally {
    dispatch({ type: 'SET_IS_COPY_LOADING', payload: false });
  }
}

/**
 * Function to handle iOS-specific clipboard operations
 */
export function iosCopyToClipboard(contentToCopy?: string): void {
  try {
    const input = document.getElementById('ios-copy') as HTMLInputElement;
    
    if (!input) {
      logger.error('Copy input element not found', new Error('Copy input element not found'), null, {
        component: 'ProductDesignForm',
        method: 'iosCopyToClipboard'
      });
      throw new Error('Copy element not found');
    }
    
    // If content is provided directly, use it; otherwise use the input's current value
    if (contentToCopy) {
      // Set the value directly for immediate use
      input.value = contentToCopy;
    }
    
    // Ensure the input is visible and focusable temporarily for selection
    const originalStyle = input.style.cssText;
    input.style.position = 'fixed';
    input.style.left = '-9999px';
    input.style.opacity = '1';
    
    // Focus and select the content
    input.focus();
    input.select();
    
    // For iOS devices, use setSelectionRange
    const isIOSDevice = navigator.userAgent.match(/ipad|iphone/i);
    if (isIOSDevice) {
      input.setSelectionRange(0, input.value.length);
    }
    
    // Try modern clipboard API first, then fallback to execCommand
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(input.value).then(() => {
        logger.info('Content copied using modern clipboard API', {
          contentLength: contentToCopy?.length || 0
        }, {
          component: 'ProductDesignForm',
          method: 'iosCopyToClipboard'
        });
      }).catch(() => {
        // Fallback to execCommand
        const success = document.execCommand("copy");
        logger.info('Content copied using execCommand (fallback)', {
          success,
          contentLength: input.value.length
        }, {
          component: 'ProductDesignForm',
          method: 'iosCopyToClipboard'
        });
      });
    } else {
      // Fallback to execCommand
      const success = document.execCommand("copy");
      logger.info('Content copied using execCommand (legacy)', {
        success,
        contentLength: input.value.length
      }, {
        component: 'ProductDesignForm',
        method: 'iosCopyToClipboard'
      });
    }
    
    // Restore original styling
    input.style.cssText = originalStyle;
    
  } catch (error) {
    logger.error('iosCopyToClipboard failed', error as Error, null, {
      component: 'ProductDesignForm',
      method: 'iosCopyToClipboard'
    });
    throw error;
  }
}

/**
 * Function to track successful design copy operation
 */
async function trackDesignCopySuccess(designSessionId: number, apiService: ApiService): Promise<void> {
  try {
    const appInsights = getAppInsights();
    
    // Log the copy success event
    appInsights?.trackEvent({ 
      name: 'useraction:DesignCopySuccess', 
      properties: { 
        'timestamp': new Date().toISOString(),
        'designSessionId': designSessionId.toString()
      } 
    });

    // Send API call to backend to update DesignCopied flag
    const response = await apiService.postAsync(
      { designCopied: true }, // Send as object
      `DesignSessions/positions/${designSessionId}/design-copied`
    );

    if (!response.ok) {
      logger.warn('Failed to update design copy status in backend', {
        designSessionId,
        status: response.status,
        statusText: response.statusText,
        component: 'ProductDesignForm',
        method: 'trackDesignCopySuccess'
      });
    } else {
      logger.info('Successfully updated design copy status in backend', {
        designSessionId,
        component: 'ProductDesignForm',
        method: 'trackDesignCopySuccess'
      });
    }
  } catch (error) {
    logger.error('Error tracking design copy success', error as Error, {
      designSessionId,
      component: 'ProductDesignForm',
      method: 'trackDesignCopySuccess'
    });
  }
}